<template>
  <!--
    The reading's own illustration, drawn off `SensorKind` (see
    `deviceWidgetTypes.sensorKindOf`) and driven by the value the tile shows.

    Nine readings — temperature, pressure, CO₂, air quality, illuminance, power,
    voltage, current and speed — share one *instrument*: a half-turn dial whose
    needle and coloured arc both sit on the value's own share. The colour is the
    reading's own scale, not decoration: temperature runs blue to red, a battery
    red to green, CO₂ and air quality green to red as the air turns. Everything
    else keeps its own drawing, tinted where the reading is a colour by nature
    (a humidity drop, water in a tank, rain, a noise meter).

    These move whenever the tile is on screen — a reading is always live, unlike a
    binary sensor's state — and the speed of each motion follows the value, so a
    gusty day visibly spins the anemometer faster than a calm one.

    `generic` draws nothing on purpose — the tile keeps the device's own glyph.
  -->
  <svg
    class="reading-anim"
    :class="`reading-anim--${kind}`"
    :style="{
      '--flow': flow,
      '--pulse': pulse,
      '--angle': angle,
      '--water': water,
    }"
    viewBox="0 0 48 48"
    aria-hidden="true"
    focusable="false">
    <defs>
      <clipPath :id="bodyClipId">
        <rect x="11" y="17" width="26" height="14" rx="2" />
      </clipPath>
      <clipPath :id="dropClipId">
        <path d="M24,9 C29.5,17 33,21.5 33,27.5 a9,9 0 0 1 -18,0 C15,21.5 18.5,17 24,9 Z" />
      </clipPath>
      <clipPath :id="tankClipId">
        <rect x="15" y="14" width="18" height="22" rx="1.6" />
      </clipPath>
    </defs>

    <!-- ── Battery: the charge it holds, with the level's own shimmer ────── -->
    <g v-if="kind === 'battery'">
      <rect class="reading-anim__ghost" x="10" y="16" width="28" height="16" rx="2.6" />
      <rect class="reading-anim__solid" x="38" y="21" width="3.4" height="6" rx="1.2" />
      <rect
        class="reading-anim__fill"
        x="12"
        y="18"
        :width="batteryFillWidth"
        height="12"
        rx="1.6"
        :style="{ fill: valueColor }" />
      <g :clip-path="`url(#${bodyClipId})`">
        <rect class="reading-anim__sheen reading-anim__sheen--sweep" x="9" y="16.5" width="4.6" height="15" rx="2" />
      </g>
    </g>

    <!-- ── Every reading that is a dial: temperature (blue through red),
         pressure, CO₂ and air quality (green through red), illuminance, power,
         voltage, current and speed. One instrument, modern-digital: a ¾ ring
         fills with the reading's own share in the reading's own colour, small
         tick marks step round it, and the number itself sits in the middle —
         there is no needle, because the number is the reading. ───────────── -->
    <g v-else-if="isDial">
      <path class="reading-anim__dial-arc" :d="DIAL_ARC" />
      <path
        class="reading-anim__dial-glow"
        :d="DIAL_ARC"
        :style="{ stroke: valueColor }"
        :stroke-dasharray="`${dialDash} ${DIAL_LENGTH.toFixed(2)}`" />
      <path
        class="reading-anim__dial-value"
        :d="DIAL_ARC"
        :style="{ stroke: valueColor }"
        :stroke-dasharray="`${dialDash} ${DIAL_LENGTH.toFixed(2)}`" />
      <g class="reading-anim__dial-ticks">
        <path v-for="tick in DIAL_TICKS" :key="tick.key" :d="tick.d" />
      </g>
      <circle v-for="cap in DIAL_CAPS" :key="cap.key" class="reading-anim__dial-cap" :cx="cap.x" :cy="cap.y" r="1.5" />
      <text
        class="reading-anim__dial-value-text"
        x="24"
        y="27"
        :style="{ fill: valueColor, fontSize: `${dialValueSize}px` }">
        {{ dialValue }}
      </text>
      <text class="reading-anim__dial-unit" x="24" y="34.6">{{ dialLabel }}</text>
    </g>

    <!-- ── Humidity: how full the drop is, and what it lets go ───────────── -->
    <g v-else-if="kind === 'humidity'">
      <path class="reading-anim__ghost" d="M24,9 C29.5,17 33,21.5 33,27.5 a9,9 0 0 1 -18,0 C15,21.5 18.5,17 24,9 Z" />
      <g :clip-path="`url(#${dropClipId})`">
        <rect
          class="reading-anim__fill"
          x="8"
          :y="dropFillY"
          width="32"
          :height="dropFillHeight"
          :style="{ fill: valueColor }" />
      </g>
      <path
        class="reading-anim__drop"
        d="M24,40.6 c-1.5,2 -2.2,2.9 -2.2,4 a2.2,2.2 0 0 0 4.4,0 c0,-1.1 -0.7,-2 -2.2,-4 Z" />
    </g>

    <!-- ── Energy: a retro meter's counter — five digits, the last of them the
         tenths, reading the number itself. ───────────────────────────────── -->
    <g v-else-if="kind === 'energy'">
      <rect class="reading-anim__odo-case" x="5.6" y="14.4" width="36.8" height="19.2" rx="3.4" />
      <g v-for="(digit, index) in energyDigits" :key="index">
        <rect class="reading-anim__odo-cell" :x="8 + index * 6.6" y="17.8" width="5.6" height="12.4" rx="1.1" />
        <text class="reading-anim__odo-digit" :x="10.8 + index * 6.6" y="27.2">{{ digit }}</text>
      </g>
      <circle class="reading-anim__odo-pulse" cx="24" cy="36.6" r="1.2" />
    </g>

    <!-- ── Wind speed is the dial too — only its direction has a drawing of its
         own. ─────────────────────────────────────────────────────────────── -->

    <!-- ── Wind direction: a masthead Windex — the compass flower underneath,
         the fixed fork it is read against, and the vane itself turning on its
         pivot: pointer forward, fin aft. ─────────────────────────────────── -->
    <g v-else-if="kind === 'windDirection'">
      <g class="reading-anim__rose">
        <path d="M24,10 L26.8,26.4 L24,42.8 L21.2,26.4 Z" />
        <path d="M7.6,26.4 L24,23.6 L40.4,26.4 L24,29.2 Z" />
      </g>
      <g class="reading-anim__rose reading-anim__rose--diag">
        <path d="M24,16.4 L26.2,26.4 L24,36.4 L21.8,26.4 Z" />
        <path d="M14.4,26.4 L24,24.2 L33.6,26.4 L24,28.6 Z" />
      </g>
      <g class="reading-anim__needle reading-anim__needle--vane">
        <path class="reading-anim__vane-arm" d="M24,35 V15" :style="{ stroke: valueColor }" />
        <path class="reading-anim__vane-head" d="M24,9.8 L26.9,17.4 H21.1 Z" :style="{ fill: valueColor }" />
        <path class="reading-anim__vane-fin" d="M23.5,28.4 L28.2,37.4 L23.5,35 Z" :style="{ fill: valueColor }" />
        <path class="reading-anim__vane-fin" d="M24.5,28.4 L19.8,37.4 L24.5,35 Z" :style="{ fill: valueColor }" />
      </g>
      <circle class="reading-anim__vane-pivot" cx="24" cy="26.4" r="1.7" :style="{ fill: valueColor }" />
      <circle class="reading-anim__vane-pin" cx="24" cy="26.4" r="0.65" />
    </g>

    <!-- ── Rain: what the cloud is letting go of, one drop per step of the
         reading — the count is the reading. ──────────────────────────────── -->
    <g v-else-if="kind === 'rain'">
      <!-- A flat-bottomed cloud: one smooth silhouette closed along a straight
           base, so it sits level in the tile instead of tilting. -->
      <path
        class="reading-anim__soft"
        d="M11.6,21 C11.6,17.6 14.2,15 17.4,15 C18.4,11.8 21.2,9.6 24.8,9.6 C28.6,9.6 31.6,12 32.6,15.4 C35.4,15.6 37.6,17.8 37.6,20.4 L37.6,21 Z" />
      <g class="reading-anim__rain" :style="{ stroke: valueColor }">
        <path v-for="drop in rainDrops" :key="drop" class="reading-anim__rain-drop" :d="RAIN_DROPS[drop]" />
      </g>
      <path class="reading-anim__ghost" d="M9,41 H39" />
    </g>

    <!-- ── Water level: the tank, and the wave on what is in it ──────────── -->
    <g v-else-if="kind === 'waterLevel'">
      <rect class="reading-anim__ghost" x="13" y="12" width="22" height="26" rx="2.6" />
      <g :clip-path="`url(#${tankClipId})`">
        <rect
          class="reading-anim__fill"
          x="14"
          :y="tankFillY"
          width="20"
          :height="tankFillHeight"
          :style="{ fill: valueColor }" />
        <path
          class="reading-anim__wave-line"
          :d="`M14,${tankFillY} q4,-1.8 8,0 t8,0 t8,0`"
          :style="{ stroke: valueColor }" />
      </g>
      <path class="reading-anim__ghost" d="M13,38 H35" />
    </g>

    <!-- ── Signal: the bars it reaches, and the one it is testing ────────── -->
    <g v-else-if="kind === 'signal'">
      <g class="reading-anim__bars">
        <rect class="reading-anim__ghost" x="13" y="29" width="4.6" height="8" rx="1.4" />
        <rect class="reading-anim__ghost" x="19.6" y="25" width="4.6" height="12" rx="1.4" />
        <rect class="reading-anim__ghost" x="26.2" y="21" width="4.6" height="16" rx="1.4" />
        <rect class="reading-anim__ghost" x="32.8" y="17" width="4.6" height="20" rx="1.4" />
        <rect v-if="signalBars > 0" class="reading-anim__fill" x="13" y="29" width="4.6" height="8" rx="1.4" />
        <rect v-if="signalBars > 1" class="reading-anim__fill" x="19.6" y="25" width="4.6" height="12" rx="1.4" />
        <rect v-if="signalBars > 2" class="reading-anim__fill" x="26.2" y="21" width="4.6" height="16" rx="1.4" />
        <rect v-if="signalBars > 3" class="reading-anim__fill" x="32.8" y="17" width="4.6" height="20" rx="1.4" />
      </g>
    </g>

    <!-- ── Noise: a step meter — one bar a step, lit up to the level. ─────── -->
    <g v-else-if="kind === 'noise'">
      <g v-for="(bar, index) in NOISE_BARS" :key="index">
        <rect class="reading-anim__noise-step" :x="bar.x" :y="bar.y" :width="bar.w" :height="bar.h" rx="1.2" />
        <rect
          v-if="index < noiseSteps"
          class="reading-anim__noise-step reading-anim__noise-step--lit"
          :x="bar.x"
          :y="bar.y"
          :width="bar.w"
          :height="bar.h"
          rx="1.2"
          :style="{ fill: valueColor }" />
      </g>
    </g>

    <!-- ── Distance: the plan it measured — two walls, and the dimension between
         them carrying the number, so the gap IS the distance. ─────────────── -->
    <g v-else-if="kind === 'distance'">
      <path class="reading-anim__wall" d="M12,15 V35" />
      <path class="reading-anim__wall" :d="`M${distanceRight},15 V35`" />
      <path class="reading-anim__dim-line" :d="`M12,25 H${distanceRight}`" />
      <path class="reading-anim__dim-tick" d="M12,23.4 V26.6" />
      <path class="reading-anim__dim-tick" :d="`M${distanceRight},23.4 V26.6`" />
      <text class="reading-anim__dim-label" :x="distanceMid" y="12.4">{{ readingLabel }}</text>
    </g>

    <!-- ── Flow: a water meter, in line with its own pipe ──────────────────
         The riser runs through the meter's housing: water moves in the pipe
         while something flows, the whole meter breathes while it measures, and
         inside the bezel the litre dial turns — one turn per litre, a real
         meter's gearing, so its pace IS the flow. -->
    <g
      v-else-if="kind === 'flow'"
      class="reading-anim__meter"
      :class="{ 'reading-anim__meter--flowing': litresPerMinute > 0 }">
      <!-- The riser, and the water in it (shown while something is flowing) -->
      <path class="reading-anim__meter-pipe" d="M24,48 V0" />
      <path class="reading-anim__meter-wall" d="M20.8,48 V38.4" />
      <path class="reading-anim__meter-wall" d="M27.2,48 V38.4" />
      <path class="reading-anim__meter-wall" d="M20.8,9.6 V0" />
      <path class="reading-anim__meter-wall" d="M27.2,9.6 V0" />
      <g class="reading-anim__water" :class="{ 'reading-anim__water--flowing': litresPerMinute > 0 }">
        <path class="reading-anim__water-body" d="M24,48 V0" />
        <path class="reading-anim__water-flow" d="M24,48 V0" />
      </g>

      <!-- The meter: its housing, the bezel, and the dial inside them -->
      <circle class="reading-anim__meter-body" cx="24" cy="24" r="14" />
      <circle class="reading-anim__meter-bezel" cx="24" cy="24" r="12.1" />
      <circle class="reading-anim__face" cx="24" cy="24" r="10.8" />
      <path v-for="tick in FLOW_TICKS" :key="tick.key" class="reading-anim__tick" :d="tick.d" />
      <g ref="handRef" class="reading-anim__hand">
        <path d="M24,15.4 L25,22.4 L24,27.2 L23,22.4 Z" />
      </g>
      <circle class="reading-anim__hub" cx="24" cy="24" r="1.3" />
    </g>
  </svg>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import type { SensorKind } from '../deviceWidgetTypes';
import { flowLitresPerMinute } from './flowDial';

const props = withDefaults(
  defineProps<{
    kind?: SensorKind;
    /** The reading the tile shows — it sets every level and every speed. */
    value?: number | null;
    /** `unit` of that reading, so a flow can be read as litres per minute. */
    unit?: string | null;
  }>(),
  { kind: 'generic', value: null, unit: null },
);

/** The span each reading is normally seen over, for the level it draws. */
const SENSOR_RANGES: Record<SensorKind, [number, number]> = {
  battery: [0, 100],
  temperature: [-10, 40],
  humidity: [0, 100],
  pressure: [950, 1050],
  co2: [400, 2000],
  air: [0, 300],
  illuminance: [0, 2000],
  power: [0, 5000],
  energy: [0, 10000],
  voltage: [200, 260],
  current: [0, 32],
  wind: [0, 25],
  windDirection: [0, 360],
  rain: [0, 20],
  waterLevel: [0, 100],
  signal: [0, 100],
  noise: [30, 90],
  distance: [0, 10],
  speed: [0, 200],
  flow: [0, 50],
  generic: [0, 1],
};

/** Decibels below zero are a signal strength, not a level — read the other way. */
const range = computed<[number, number]>(() => {
  if (props.kind === 'signal' && typeof props.value === 'number' && props.value < 0) return [-100, -40];
  return SENSOR_RANGES[props.kind];
});

const level = computed(() => {
  if (typeof props.value !== 'number' || Number.isNaN(props.value)) return 0.5;
  const [min, max] = range.value;
  if (max === min) return 0.5;
  return Math.min(1, Math.max(0, (props.value - min) / (max - min)));
});

/** Faster the higher the reading is: heavy rain falls faster than drizzle. */
const flow = computed(() => `${(1.8 - 1.5 * level.value).toFixed(2)}s`);
const pulse = computed(() => `${(2.4 - 1.8 * level.value).toFixed(2)}s`);

/** A vane turns to the degrees themselves, not to a share of its span. */
const angle = computed(() => `${((props.value ?? 0) % 360 || 0).toFixed(1)}deg`);

/**
 * The hues each reading owns. A temperature runs blue to red, a battery red to
 * green, CO₂ and air quality green to red as they turn, and the rest sit on the
 * one tone their unit is read in — pressure blue, power amber, voltage indigo,
 * current orange, speed cyan.
 */
const valueColor = computed(() => {
  const share = level.value;
  switch (props.kind) {
    case 'temperature':
      return `hsl(${(215 - 215 * share).toFixed(0)} 78% 52%)`;
    case 'battery':
      return `hsl(${(4 + 116 * share).toFixed(0)} 72% 46%)`;
    case 'co2':
    case 'air': {
      const worse = Math.min(1, Math.max(0, (share - 0.35) / 0.5));
      return `hsl(${(128 - 122 * worse).toFixed(0)} 72% 48%)`;
    }
    case 'illuminance':
      return 'hsl(45 92% 52%)';
    case 'power':
      return 'hsl(32 92% 52%)';
    case 'voltage':
      return 'hsl(250 70% 62%)';
    case 'current':
      return 'hsl(14 82% 54%)';
    case 'speed':
    case 'wind':
    case 'windDirection':
      return 'hsl(190 78% 46%)';
    case 'pressure':
      return 'hsl(205 58% 52%)';
    case 'humidity':
    case 'waterLevel':
      return 'hsl(200 80% 50%)';
    case 'rain':
      return 'hsl(205 82% 54%)';
    case 'noise':
      return 'hsl(268 62% 58%)';
    default:
      return 'currentColor';
  }
});

/** The ten readings drawn as one instrument. */
const DIAL_KINDS: readonly SensorKind[] = [
  'temperature',
  'pressure',
  'co2',
  'air',
  'illuminance',
  'power',
  'voltage',
  'current',
  'wind',
  'speed',
];
const isDial = computed(() => DIAL_KINDS.includes(props.kind));

/** The dial: a ¾ ring about (24, 24), opening at the bottom. */
const DIAL_RADIUS = 16;
const DIAL_START = 135;
const DIAL_SWEEP = 270;
const DIAL_LENGTH = (2 * Math.PI * DIAL_RADIUS * DIAL_SWEEP) / 360;

/** A point on the ring, `share` of the way round, at `radius`. */
const dialPoint = (share: number, radius = DIAL_RADIUS) => {
  const angle = ((DIAL_START + DIAL_SWEEP * share) * Math.PI) / 180;
  return { x: 24 + radius * Math.cos(angle), y: 24 + radius * Math.sin(angle) };
};
const dialAt = (share: number, radius = DIAL_RADIUS) => {
  const point = dialPoint(share, radius);
  return `${point.x.toFixed(2)},${point.y.toFixed(2)}`;
};

/** Start at the lower left, sweep over the top, end at the lower right. */
const DIAL_ARC = `M${dialAt(0)} A${DIAL_RADIUS},${DIAL_RADIUS} 0 1 1 ${dialAt(1)}`;

/** Four steps along the ring, so a share can be read without the needle. */
const DIAL_TICKS = [0, 0.25, 0.5, 0.75, 1].map((share) => ({
  key: share,
  d: `M${dialAt(share, DIAL_RADIUS - 3.4)} L${dialAt(share, DIAL_RADIUS - 0.6)}`,
}));

/** The two ends of the ring, capped like a real instrument's scale. */
const DIAL_CAPS = [0, 1].map((share) => ({ key: share, ...dialPoint(share) }));

const dialDash = computed(() => (DIAL_LENGTH * level.value).toFixed(2));

/**
 * The number in the middle of the ring — the reading itself, whole units past
 * ten and one decimal below it, with no thousands separator: at tile size a
 * comma is a smudge, and the ring already gives the magnitude.
 */
const dialValue = computed(() => {
  const value = props.value;
  if (typeof value !== 'number' || Number.isNaN(value)) return '—';
  const shown = Math.abs(value) >= 10 ? Math.round(value) : Math.round(value * 10) / 10;
  return String(shown);
});

/** The number shrinks as it grows, so it always sits inside the ring. */
const dialValueSize = computed(() => {
  const digits = dialValue.value.replace('-', '').length;
  if (digits <= 2) return 14;
  if (digits === 3) return 12;
  if (digits === 4) return 9.6;
  return 8.2;
});

/** The unit under the number, so a bare gauge still says what it measures. */
const dialLabel = computed(() => (props.unit ?? '').toString());

/** A battery, a temperature, a level: the number itself, as the tile has it. */
const readingLabel = computed(() => {
  const value = props.value;
  if (typeof value !== 'number' || Number.isNaN(value)) return '—';
  const digits = Math.abs(value) >= 100 ? 0 : 1;
  const unit = (props.unit ?? '').toString();
  return `${value.toFixed(digits)}${unit ? ` ${unit}` : ''}`;
});

/** The counter's five digits — a real meter reads its whole span, zeros and all. */
const energyDigits = computed(() => {
  const value = Math.max(0, Math.round(props.value ?? 0));
  return String(value % 100000)
    .padStart(5, '0')
    .split('');
});

/** A step meter of six bars; the reading lights the ones it has reached. */
const NOISE_BARS = Array.from({ length: 6 }, (_, index) => {
  const height = 8 + index * 4;
  return { x: 6.8 + index * 6, y: 38 - height, w: 4.4, h: height };
});
const noiseSteps = computed(() => Math.max(1, Math.round(level.value * NOISE_BARS.length)));

/** Six drops, in the order a rising rain draws them in. */
const RAIN_DROPS = [
  'M17,25 l-1.4,4',
  'M25,26 l-1.4,4',
  'M33,25 l-1.4,4',
  'M21,33 l-1.4,4',
  'M29,33 l-1.4,4',
  'M13,33 l-1.4,4',
];
const rainDrops = computed(() =>
  Array.from({ length: Math.max(1, Math.round(level.value * RAIN_DROPS.length)) }, (_, index) => index),
);

const batteryFillWidth = computed(() => Math.max(1, 24 * level.value));
const dropFillY = computed(() => 37 - 27 * level.value);
const dropFillHeight = computed(() => 40 - dropFillY.value);
const tankFillY = computed(() => 36.5 - 23 * level.value);
const tankFillHeight = computed(() => 37 - tankFillY.value);
const signalBars = computed(() => Math.round(level.value * 4));

/** Distance: the second wall stands where the reading puts it, 18 → 38. */
const distanceRight = computed(() => (18 + 20 * level.value).toFixed(1));
/** The dimension's own middle, so the number sits over the line it labels. */
const distanceMid = computed(() => (6 + Number(distanceRight.value) / 2).toFixed(1));

/** The litre dial's ten marks — 0.1 L apart, the one under the hand longer. */
const FLOW_TICKS = Array.from({ length: 10 }, (_, index) => {
  const angle = ((index * 36) / 180) * Math.PI;
  const inner = index === 0 ? 7.6 : 8.7;
  const sin = Math.sin(angle);
  const cos = Math.cos(angle);
  const from = `${(24 + sin * inner).toFixed(2)},${(24 - cos * inner).toFixed(2)}`;
  const to = `${(24 + sin * 9.9).toFixed(2)},${(24 - cos * 9.9).toFixed(2)}`;
  return { key: index, d: `M${from} L${to}` };
});

/** The reading in litres a minute — the pace the litre hand turns at. */
const litresPerMinute = computed(() => flowLitresPerMinute(props.value, props.unit));

/** The water in the pipe runs faster the more is flowing through it. */
const water = computed(() => {
  const litres = litresPerMinute.value;
  return `${Math.min(2.2, Math.max(0.5, 8 / litres)).toFixed(2)}s`;
});

/**
 * The litre hand is not a looping keyframe: its angle is the water that has
 * gone through it, so it never jumps when the reading refreshes and its pace is
 * the flow itself — a real meter's hand turns once per litre, i.e. 6° a second
 * for every litre a minute. No flow and no motion preference means no loop.
 */
const handRef = ref<SVGGElement | null>(null);
let handFrame = 0;
let handAngle = 0;
let handStamp = 0;
let handStill = false;
let handMotion: MediaQueryList | null = null;

function stepHand(stamp: number) {
  const hand = handRef.value;
  const litres = handStill ? 0 : litresPerMinute.value;
  // A tab that slept must not whip the hand forward when it wakes up.
  const seconds = handStamp === 0 ? 0 : Math.min(0.25, (stamp - handStamp) / 1000);
  handStamp = stamp;

  if (hand && litres > 0) {
    handAngle = (handAngle + seconds * litres * 6) % 360;
    hand.style.transform = `rotate(${handAngle.toFixed(2)}deg)`;
  }

  // A still hand is left where it stopped, holding its frame until flow returns.
  handFrame = handStill || litres <= 0 ? 0 : requestAnimationFrame(stepHand);
}

function onHandMotionChange(event: MediaQueryListEvent) {
  handStill = event.matches;
  handStamp = 0;
  if (!handStill) startHand();
}

function startHand() {
  if (props.kind !== 'flow' || handFrame || typeof window === 'undefined') return;
  if (!handMotion) {
    handMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    handStill = handMotion.matches;
    handMotion.addEventListener('change', onHandMotionChange);
  }
  handStamp = 0;
  handFrame = requestAnimationFrame(stepHand);
}

function stopHand() {
  if (handFrame) cancelAnimationFrame(handFrame);
  handFrame = 0;
  handMotion?.removeEventListener('change', onHandMotionChange);
  handMotion = null;
}

watch(litresPerMinute, (litres) => {
  if (litres > 0) startHand();
});
watch(
  () => props.kind === 'flow',
  (running) => (running ? startHand() : stopHand()),
);
onMounted(() => {
  if (props.kind === 'flow') startHand();
});
onBeforeUnmount(stopHand);

let instanceSeq = 0;
const uid = `reading-anim-${++instanceSeq}`;
const bodyClipId = `${uid}-body`;
const dropClipId = `${uid}-drop`;
const tankClipId = `${uid}-tank`;
</script>

<style scoped>
.reading-anim {
  display: block;
  width: 100%;
  height: 100%;
}

/* A body, a blade, a cloud: tinted so the drawing reads as an object */
.reading-anim__soft {
  fill: currentColor;
  fill-opacity: 0.26;
  stroke: currentColor;
  stroke-width: 1.3;
  stroke-linejoin: round;
}

.reading-anim__solid {
  fill: currentColor;
}

/* What the reading fills: the charge, the mercury, the water */
.reading-anim__fill {
  fill: currentColor;
  fill-opacity: 0.5;
}

/* A fill that carries the reading's own colour has to be solid enough to read
   as that colour, not as a wash of it. */
.reading-anim__fill[style] {
  fill-opacity: 0.9;
}

/* Wires, dials, the room the device sits in */
.reading-anim__ghost {
  fill: none;
  stroke: currentColor;
  stroke-width: 1.4;
  stroke-linecap: round;
  stroke-linejoin: round;
  opacity: 0.45;
}

/* What the device is doing — rings, gusts, a stream */
.reading-anim__sheen {
  fill: rgba(255, 255, 255, 0.45);
}

/* ── The dial ─────────────────────────────────────────────────────────────
   Modern digital, not mechanical: a ¾ ring of track, the value's own share of
   it in the reading's colour, four tick steps, and the number in the middle.
   No needle — the number IS the reading, and the ring is its shape. */
.reading-anim__dial-arc {
  fill: none;
  stroke: currentColor;
  stroke-width: 3;
  stroke-linecap: round;
  opacity: 0.18;
}

.reading-anim__dial-value {
  fill: none;
  stroke-width: 3;
  stroke-linecap: round;
  transition: stroke-dasharray 0.9s cubic-bezier(0.2, 0.8, 0.2, 1);
}

/* The same share again, wide and faint: the reading glows rather than sits on
   the dial, which is what makes it read as a lit instrument. */
.reading-anim__dial-glow {
  fill: none;
  stroke-width: 7;
  stroke-linecap: round;
  opacity: 0.22;
  transition: stroke-dasharray 0.9s cubic-bezier(0.2, 0.8, 0.2, 1);
}

.reading-anim__dial-ticks path {
  fill: none;
  stroke: currentColor;
  stroke-width: 1.1;
  stroke-linecap: round;
  opacity: 0.45;
}

.reading-anim__dial-cap {
  fill: currentColor;
  opacity: 0.3;
}

/* Tabular figures, so a changing reading does not shuffle its own digits. */
.reading-anim__dial-value-text {
  font-family: ui-monospace, 'SF Mono', Menlo, monospace;
  font-size: 14px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.4px;
  text-anchor: middle;
}

.reading-anim__dial-unit {
  font-size: 6.2px;
  font-weight: 600;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  text-anchor: middle;
  fill: currentColor;
  opacity: 0.55;
}

/* ── The energy counter: retro digits on a dark window, amber like a meter ── */
.reading-anim__odo-case {
  fill: #1f2937;
  stroke: #0f172a;
  stroke-width: 1.2;
}

.reading-anim__odo-cell {
  fill: #0b1220;
}

.reading-anim__odo-digit {
  font-family: ui-monospace, 'SF Mono', Menlo, monospace;
  font-size: 9.6px;
  font-weight: 700;
  text-anchor: middle;
  fill: #fbbf24;
}

.reading-anim__odo-pulse {
  fill: #fbbf24;
  animation: sensor-blink var(--pulse, 2s) ease-in-out infinite;
}

/* ── Wind direction: a masthead Windex ─────────────────────────────────────
   The compass flower sits underneath as the scale, the fork is fixed to the
   masthead, and the vane turns on its pivot — pointer forward, fin aft, so it
   reads the way the one at the top of a mast does. */
.reading-anim__rose {
  fill: currentColor;
  opacity: 0.14;
}

.reading-anim__rose--diag {
  opacity: 0.085;
}

.reading-anim__vane-arm {
  fill: none;
  stroke-width: 2;
  stroke-linecap: round;
}

.reading-anim__vane-head,
.reading-anim__vane-fin {
  stroke: none;
}

.reading-anim__vane-pivot {
  stroke: none;
}

/* The pin in the middle of the pivot, in the page's own ink, so the vane reads
   as something mounted rather than as a shape lying on the rose. */
.reading-anim__vane-pin {
  fill: currentColor;
}

/* ── Noise: a step meter ─────────────────────────────────────────────────── */
.reading-anim__noise-step {
  fill: none;
  stroke: currentColor;
  stroke-width: 1.3;
  opacity: 0.35;
}

.reading-anim__noise-step--lit {
  stroke: none;
  opacity: 1;
  transition: opacity 0.35s ease;
}

/* ── Distance: walls on a plan, and the dimension beside them. The dimension is
      a hairline with short end ticks, so the walls stay the loudest thing. ── */
.reading-anim__wall {
  fill: none;
  stroke: currentColor;
  stroke-width: 3.4;
  stroke-linecap: round;
}

.reading-anim__dim-line {
  fill: none;
  stroke: currentColor;
  stroke-width: 1;
  stroke-linecap: round;
  opacity: 0.35;
}

.reading-anim__dim-tick {
  fill: none;
  stroke: currentColor;
  stroke-width: 1;
  stroke-linecap: round;
  opacity: 0.35;
}

.reading-anim__dim-label {
  font-size: 8px;
  font-weight: 700;
  text-anchor: middle;
  fill: currentColor;
}

/* ── Rain: the drops take the reading's own blue ─────────────────────────── */
.reading-anim__rain-drop {
  fill: none;
  stroke-width: 2.2;
  stroke-linecap: round;
  stroke-dasharray: none;
}

.reading-anim__wave-line {
  fill: none;
  stroke: currentColor;
  stroke-width: 1.6;
  stroke-linecap: round;
  opacity: 0.7;
  animation: sensor-bob 2.6s ease-in-out infinite;
}

/* ── The water meter: a real meter's own colours, the one drawing that has
   them — a metal housing with a white dial inside, and the riser it sits in.
   That contrast is what makes the pace of the hand readable at tile size. */
.reading-anim__meter-pipe {
  fill: none;
  stroke: color-mix(in srgb, currentColor 30%, transparent);
  stroke-width: 6.4;
}

.reading-anim__meter-wall {
  fill: none;
  stroke: color-mix(in srgb, currentColor 70%, transparent);
  stroke-width: 1.2;
  stroke-linecap: round;
}

.reading-anim__water-body {
  fill: none;
  stroke: #38bdf8;
  stroke-width: 3.6;
}

.reading-anim__water {
  opacity: 0;
  transition: opacity 0.45s ease;
}

.reading-anim__water--flowing {
  opacity: 1;
}

.reading-anim__water-flow {
  fill: none;
  stroke: #e0f2fe;
  stroke-width: 1.4;
  stroke-linecap: round;
  stroke-dasharray: 3 5;
}

.reading-anim__water--flowing .reading-anim__water-flow {
  animation: sensor-flow var(--water, 1.2s) linear infinite;
}

.reading-anim__meter-body {
  fill: #cbd5e1;
  stroke: #64748b;
  stroke-width: 1.4;
}

/* Water moving through a meter is live, so the whole meter breathes while it
   measures — the same warning the valve gives while it is open. */
.reading-anim__meter--flowing {
  animation: sensor-meter-breathe 2.2s ease-in-out infinite;
}

@keyframes sensor-meter-breathe {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.55;
  }
}

.reading-anim__meter-bezel {
  fill: none;
  stroke: #94a3b8;
  stroke-width: 1;
  opacity: 0.65;
}

.reading-anim__face {
  fill: #ffffff;
  stroke: #94a3b8;
  stroke-width: 1.2;
}

.reading-anim__tick {
  fill: none;
  stroke: #475569;
  stroke-width: 1;
  stroke-linecap: round;
}

.reading-anim__hand {
  transform-box: view-box;
  transform-origin: 24px 24px;
  fill: #dc2626;
}

.reading-anim__hub {
  fill: #1e293b;
}

/* ── The motions ─────────────────────────────────────────────────────────
   Every speed comes from the reading itself (`--flow`, `--pulse`), so the tile
   shows not just what is measured but how much of it there is. */
.reading-anim__needle,
.reading-anim__rain,
.reading-anim__drop {
  transform-box: view-box;
}

/* A wind vane turns about its own pivot, and only to what it measures */
.reading-anim__needle--vane {
  transform-origin: 24px 26.4px;
  transform: rotate(var(--angle, 0deg));
  transition: transform 0.9s cubic-bezier(0.2, 0.8, 0.2, 1);
}

.reading-anim__drop {
  transform-origin: 24px 40px;
  animation: sensor-drip 2.6s ease-in infinite;
}

.reading-anim__sheen--sweep {
  animation: sensor-sheen 2.8s ease-in-out infinite;
}

@keyframes sensor-bob {
  0%,
  100% {
    transform: translate(0, 0);
  }
  50% {
    transform: translate(1.5px, -0.6px);
  }
}

@keyframes sensor-blink {
  0%,
  100% {
    opacity: 0.35;
  }
  50% {
    opacity: 1;
  }
}

@keyframes sensor-drip {
  0%,
  70% {
    transform: translateY(0);
    opacity: 0;
  }
  78% {
    opacity: 0.95;
  }
  100% {
    transform: translateY(4.5px);
    opacity: 0;
  }
}

@keyframes sensor-sheen {
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(30px);
  }
}

/* The water's own current, one dash-period per `--water` seconds */
@keyframes sensor-flow {
  to {
    stroke-dashoffset: -8;
  }
}

/* The rain falls in step with the reading, one drop-loop per step */
@keyframes sensor-fall {
  0% {
    transform: translateY(-2.4px);
    opacity: 0;
  }
  25% {
    opacity: 0.95;
  }
  100% {
    transform: translateY(6px);
    opacity: 0;
  }
}

.reading-anim__rain-drop {
  animation: sensor-fall var(--flow, 1.6s) ease-in infinite;
}

.reading-anim__rain-drop:nth-child(2n) {
  animation-delay: 0.3s;
}

.reading-anim__rain-drop:nth-child(3n) {
  animation-delay: 0.6s;
}

@media (prefers-reduced-motion: reduce) {
  .reading-anim__needle,
  .reading-anim__rain-drop,
  .reading-anim__drop,
  .reading-anim__wave-line,
  .reading-anim__water-flow,
  .reading-anim__meter,
  .reading-anim__odo-pulse,
  .reading-anim__dial-value,
  .reading-anim__dial-glow,
  .reading-anim__sheen--sweep {
    animation: none;
    transition: none;
  }
}
</style>
